import { ERROR_MESSAGES_EN, ERROR_MESSAGES_AR, LANGUAGES } from './constants';

/**
 * Get error message based on language
 */
export const getErrorMessage = (errorKey, language = LANGUAGES.AR) => {
  const messages = language === LANGUAGES.AR ? ERROR_MESSAGES_AR : ERROR_MESSAGES_EN;
  return messages[errorKey] || messages.UNKNOWN_ERROR;
};

/**
 * Format time in MM:SS format
 */
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format time in HH:MM:SS format
 */
export const formatTimeLong = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Format current time
 */
export const formatCurrentTime = () => {
  const now = new Date();
  return now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Check if browser supports required media APIs
 */
export const checkBrowserSupport = () => {
  const support = {
    mediaDevices: !!navigator.mediaDevices,
    getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
    mediaRecorder: typeof MediaRecorder !== 'undefined',
    webRTC: !!window.RTCPeerConnection,
  };
  
  support.isSupported = support.mediaDevices && support.getUserMedia && support.mediaRecorder;
  return support;
};

/**
 * Get supported video MIME type
 * Priority matches reference implementation (static/interview.html)
 */
export const getSupportedMimeType = () => {
  const types = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return null;
};

/**
 * Generate unique session ID
 */
export const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Delay utility
 */
export const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retry function with exponential backoff
 */
export const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxRetries - 1) {
        const delayTime = baseDelay * Math.pow(2, attempt);
        await delay(delayTime);
      }
    }
  }
  
  throw lastError;
};

/**
 * Check network connection
 */
export const checkNetworkConnection = () => {
  return navigator.onLine;
};

/**
 * Estimate connection quality
 */
export const getConnectionQuality = () => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  
  if (!connection) {
    return 'unknown';
  }
  
  const { effectiveType, downlink, rtt } = connection;
  
  if (effectiveType === '4g' && downlink > 5) {
    return 'excellent';
  } else if (effectiveType === '4g' || (effectiveType === '3g' && downlink > 1)) {
    return 'good';
  } else if (effectiveType === '3g') {
    return 'moderate';
  } else {
    return 'poor';
  }
};

/**
 * Convert blob to base64
 */
export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Download blob as file
 */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * Classify media error
 */
export const classifyMediaError = (error) => {
  if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
    return {
      type: 'permission',
      key: error.message?.includes('microphone') ? 'MICROPHONE_PERMISSION_DENIED' : 'CAMERA_PERMISSION_DENIED',
    };
  }
  
  if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
    return {
      type: 'device',
      key: error.message?.includes('microphone') ? 'MICROPHONE_NOT_FOUND' : 'CAMERA_NOT_FOUND',
    };
  }
  
  if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
    return {
      type: 'device',
      key: 'CAMERA_NOT_FOUND',
    };
  }
  
  if (error.name === 'OverconstrainedError') {
    return {
      type: 'constraint',
      key: 'CAMERA_NOT_FOUND',
    };
  }
  
  return {
    type: 'unknown',
    key: 'UNKNOWN_ERROR',
  };
};
